import { JourneyNode } from '../../types/journey-nodes'
import type { IVariantSplitStep } from '@pushly/models/lib/structs/journeys'
import { getBranchingData, isBranchingStepType } from '@pushly/models/lib/structs/journeys'
import type { BranchingData } from '@pushly/models/lib/structs/journeys'

/**
 * Predicate: is this node a branching node (variant split or condition)?
 * Thin wrapper that extracts node.data.step and delegates to the shared library.
 */
export function isBranchingNode(node: JourneyNode): boolean {
    return isBranchingStepType(node.data.step.type)
}

/**
 * Extract the branching data from any branching step.
 * Returns undefined for non-branching nodes.
 * Thin wrapper that extracts node.data.step and delegates to the shared library.
 */
export function getBranchingNodeData(node: JourneyNode): BranchingData | undefined {
    return getBranchingData(node.data.step)
}

/**
 * Distributes 100% across N variants.
 * First N-1 get floor(100/N), last gets the remainder.
 * Example: autoDistribute(3) → [33, 33, 34]
 */
export function autoDistribute(count: number): number[] {
    const base = Math.floor(100 / count)
    const percentages = Array(count).fill(base) as number[]
    percentages[count - 1] = 100 - base * (count - 1)
    return percentages
}

/**
 * Returns the set of step IDs reachable from one variant's out_step_id
 * that are NOT reachable from any other variant's out_step_id
 * and are not equal to joinStepId.
 *
 * Used for variant removal cleanup — identifies steps to delete.
 */
export function getExclusivelyReachableSteps(
    variantOutStepId: string | number | null,
    allVariantOutStepIds: (string | number | null)[],
    joinStepId: string | number | null,
    nodes: JourneyNode[],
    existingNodeMap?: NodeMap,
): Set<string> {
    const nodeMap = existingNodeMap ?? buildNodeMap(nodes)

    // Collect steps reachable from the target variant
    const targetReachable = getReachableSteps(variantOutStepId, joinStepId, nodeMap)

    // Collect steps reachable from all other variants
    const otherReachable = new Set<string>()
    for (const outStepId of allVariantOutStepIds) {
        if (outStepId === variantOutStepId) continue
        for (const stepId of getReachableSteps(outStepId, joinStepId, nodeMap)) {
            otherReachable.add(stepId)
        }
    }

    // Exclusive = reachable from target but not from any other variant
    const exclusive = new Set<string>()
    for (const stepId of targetReachable) {
        if (!otherReachable.has(stepId)) {
            exclusive.add(stepId)
        }
    }

    return exclusive
}

/**
 * Counts steps in a variant path from out_step_id to join_step_id (exclusive).
 * Used for the confirmation dialog step count.
 */
export function countVariantPathSteps(
    variantOutStepId: string | number | null,
    joinStepId: string | number | null,
    nodes: JourneyNode[],
): number {
    const nodeMap = buildNodeMap(nodes)
    return getReachableSteps(variantOutStepId, joinStepId, nodeMap).size
}

/**
 * Returns a Map<string, string> mapping each step ID to its owning variant ID.
 * Steps reachable from multiple variants map to a special '__shared__' key.
 * Used for cross-branch detection.
 */
export function getVariantPathOwnership(
    splitStep: JourneyNode<IVariantSplitStep>,
    nodes: JourneyNode[],
): Map<string, string> {
    const config = splitStep.data.step.configuration
    const joinStepId = config.join_step_id
    const nodeMap = buildNodeMap(nodes)
    const ownership = new Map<string, string>()

    for (const branch of config.branches) {
        const reachable = getReachableSteps(branch.out_step_id, joinStepId, nodeMap)
        for (const stepId of reachable) {
            if (ownership.has(stepId)) {
                // Already owned by another branch — mark as shared
                ownership.set(stepId, '__shared__')
            } else {
                ownership.set(stepId, branch.key)
            }
        }
    }

    return ownership
}

// ---- Exported helpers ----

export type NodeMap = Map<string, JourneyNode>

export function buildNodeMap(nodes: JourneyNode[]): NodeMap {
    const map = new Map<string, JourneyNode>()
    for (const node of nodes) {
        map.set(node.id, node)
    }
    return map
}

/**
 * Walks the out_step_id chain from startStepId, collecting step IDs
 * until reaching stopStepId (exclusive) or a dead end.
 * For branching nodes, recurses into each branch and continues from join_step_id.
 * Handles cycles by tracking visited nodes.
 */
function getReachableSteps(
    startStepId: string | number | null,
    stopStepId: string | number | null,
    nodeMap: NodeMap,
): Set<string> {
    const reachable = new Set<string>()
    if (startStepId == null) return reachable

    const stopKey = stopStepId != null ? String(stopStepId) : null
    let currentId: string | null = String(startStepId)

    while (currentId != null) {
        // Stop at join target (exclusive)
        if (currentId === stopKey) break
        // Cycle protection
        if (reachable.has(currentId)) break

        const node = nodeMap.get(currentId)
        if (!node) break

        reachable.add(currentId)

        if (isBranchingNode(node)) {
            const data = getBranchingNodeData(node)
            if (data) {
                // Recurse into each branch
                for (const branch of data.branches) {
                    if (branch.out_step_id == null) continue
                    for (const stepId of getReachableSteps(branch.out_step_id, stopKey, nodeMap)) {
                        reachable.add(stepId)
                    }
                }
                // Continue from join_step_id
                currentId = data.join_step_id != null ? String(data.join_step_id) : null
                continue
            }
        }

        // Follow the out_step_id chain for non-branching nodes
        currentId = getNodeOutStepId(node)
    }

    return reachable
}

/**
 * Gets the next step ID from a node's configuration.
 * For branching nodes, returns join_step_id (the linear continuation point).
 * For non-branching nodes, returns out_step_id.
 */
export function getNodeOutStepId(node: JourneyNode): string | null {
    if (isBranchingNode(node)) {
        const data = getBranchingNodeData(node)
        return data?.join_step_id != null ? String(data.join_step_id) : null
    }

    const config = node.data.step.configuration
    return config.out_step_id != null ? String(config.out_step_id) : null
}
